// app/lib/tracking/interactions.ts

import { axiosInstance } from "@/app/lib/api/axiosInstance";

// ===== Types =====

export type InteractableType =
  | "product"
  | "category"
  | "category_group"
  | "category_sub_group"
  | "brand"
  | "page"
  | "menu"
  | "section"
  | "seller"
  | "campaign";

export type InteractionType =
  | "view"
  | "click"
  | "add_to_cart"
  | "favorite"
  | "follow"
  | "checkout";

// Bilinen model tipleri + custom string (örn: "stories") birlikte kabul edilir.
// `(string & {})` bilinen literal'lerin autocomplete'ini korurken serbest
// string'e de izin verir — gerçek id ile custom tip event'i atmak için.
export type InteractableTypeInput = InteractableType | (string & {});

export type TrackInteractionPayload = {
  interactable_type: InteractableType;
  interactable_id: number;
  interaction_type: InteractionType;
};

// Model bazlı olmayan (custom) event'ler için payload
export type TrackGenericPayload = {
  interactable_type: string; // örn: "whatsapp", "spin_wheel", "tozy_chat", "search_bar"
  interactable_id: number;   // genellikle 0 — model yok
  interaction_type: InteractionType;
};

// ===== Core =====

/**
 * POST /general/interactions/track
 * Model bazlı event'ler (product, category, brand, seller vb.)
 * Hataları sessizce yutulur — kullanıcı akışını bozmaz.
 */
export const trackInteraction = async (
  interactable_type: InteractableTypeInput,
  interactable_id: number,
  interaction_type: InteractionType,
): Promise<void> => {
  if (typeof window === "undefined") return;
  // Gerçek id yoksa (henüz bilinmiyorsa) 0 gönderilir — model tipi yine de anlamlı kaydedilir.
  if (interactable_id === undefined || interactable_id === null) return;

  try {
    await axiosInstance.post("/general/interactions/track", {
      interactable_type,
      interactable_id,
      interaction_type,
    });
  } catch {
    // Tracking hatası kullanıcıyı etkilemez
  }
};

/**
 * POST /general/interactions/track
 * Model bazlı olmayan custom event'ler (whatsapp, spin_wheel, tozy_chat vb.)
 * interactable_id her zaman 0 — model yoktur, type string olarak kaydedilir.
 */
export const trackGeneric = async (
  interactable_type: string,
  interaction_type: InteractionType = "click",
): Promise<void> => {
  if (typeof window === "undefined") return;

  try {
    await axiosInstance.post("/general/interactions/track", {
      interactable_type,
      interactable_id: 0,
      interaction_type,
    });
  } catch {
    // Tracking hatası kullanıcıyı etkilemez
  }
};

// ===== Convenience Helpers =====

export const trackProductView = (productId: number) =>
  trackInteraction("product", productId, "view");

export const trackProductClick = (productId: number) =>
  trackInteraction("product", productId, "click");

export const trackProductAddToCart = (productId: number) =>
  trackInteraction("product", productId, "add_to_cart");

export const trackProductFavorite = (productId: number) =>
  trackInteraction("product", productId, "favorite");

export const trackProductCheckout = (productId: number) =>
  trackInteraction("product", productId, "checkout");

export const trackCategoryView = (categoryId: number) =>
  trackInteraction("category", categoryId, "view");

export const trackCategoryClick = (categoryId: number) =>
  trackInteraction("category", categoryId, "click");

export const trackBrandView = (brandId: number) =>
  trackInteraction("brand", brandId, "view");

export const trackBrandClick = (brandId: number) =>
  trackInteraction("brand", brandId, "click");

export const trackBrandFollow = (brandId: number) =>
  trackInteraction("brand", brandId, "follow");

export const trackSellerView = (sellerId: number) =>
  trackInteraction("seller", sellerId, "view");

export const trackSellerFollow = (sellerId: number) =>
  trackInteraction("seller", sellerId, "follow");

export const trackCampaignView = (campaignId: number) =>
  trackInteraction("campaign", campaignId, "view");

export const trackCampaignClick = (campaignId: number) =>
  trackInteraction("campaign", campaignId, "click");

export const trackSectionView = (sectionId: number) =>
  trackInteraction("section", sectionId, "view");

export const trackSectionClick = (sectionId: number) =>
  trackInteraction("section", sectionId, "click");